import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CodeService, type Code } from "@/services/code-service";
import { useEffect, useState } from "react";
import GoogleMapPin from "../maps/GoogleMapPin";
import { useForm } from "react-hook-form";
import { ValidationError } from "@/lib/http-client";
import { setFormErrors } from "@/lib/react-form-utils";
import { ObservationService, type Observation, } from "@/services/ObservationService";
import { useParams } from "react-router";
import FilesSelector from "../ui/FilesSelector";
import { AttachmentService, type AttachmentDetails, } from "@/services/AttachmenteService";
import { Textarea } from "../ui/textarea";
import { extractGPSFromImage, type GPSCoords } from "@/lib/image-utils";

interface NewMapObservationsProps {
  centreCoordinates: GPSCoords;
  onClose: (
    observation: Observation | null,
    attachments: AttachmentDetails[]
  ) => void;
}

const NewMapObservations: React.FC<NewMapObservationsProps> = ({ centreCoordinates, onClose,}) => {
  const params = useParams();
  const { register, handleSubmit, formState: { isDirty, errors }, reset, setError, } = useForm<Omit<Observation, "id">>();
  const [codes, setCodes] = useState<Code[]>([]);
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  const [selectedFiles, setSelectedFiles] = useState<FormData | null>(null);
  const [gps, setGps] = useState<GPSCoords | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const initPage = async () => {
      const [codes] = await Promise.all([CodeService.getCodes2()]);
      setCodes(codes.filter((c) => c.type === "ObservationType"));
    };
    initPage();
  }, []);

  useEffect(() => {
    reset({
      siteId: params.siteId,
      areaId: null,
      featureId: null,
      name: "",
      typeCodeId: codes.find((c) => c.isDefault)?.id ?? "",
      latitude: gps?.latitude ?? null,
      longitude: gps?.longitude ?? null,
      description: "",
    });
  }, [codes, params.siteId, reset]);

  useEffect(() => {
    setIsSaveDisabled(!isDirty);
  }, [isDirty]);

  const handleFormSubmit = async (data: Omit<Observation, "id">) => {
    data.latitude = gps?.latitude ?? null;
    data.longitude = gps?.longitude ?? null;
    data.featureId = null;
    try {
      setIsSaving(true)
      setIsSaveDisabled(true);

      const newObservation = await ObservationService.create(data);
      let attachments: AttachmentDetails[] = [];
      if (selectedFiles) {
        attachments = await AttachmentService.uploadAttachment(
          newObservation.id,
          selectedFiles
        );
      }
      onClose(newObservation, attachments);
    } catch (error) {
      if (error instanceof ValidationError) {
        setFormErrors<Omit<Observation, "id">>(error.errors, setError, data);
      }
    } finally {
      setIsSaveDisabled(false);
      setIsSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4 p-2">
      <div>
        <Label
          htmlFor="name"
          className="block text-sm font-medium text-gray-700"
        >
          Summary*
        </Label>
        <Input
          autoFocus
          type="text"
          {...register("name", { required: "Name is required" })}
        />
        <span className="text-sm text-red-500">{errors.name?.message}</span>
      </div>
      <div>
        <Label htmlFor="polygon-name">Observations</Label>
        <Textarea
          id="polygon-name"
          placeholder="Enter observations..."
          {...register("description")}
        />
      </div>      
      <div>
        <Label htmlFor="typeCodeId">Type</Label>
        <select
          id="typeCodeId"
          className={`flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1`}
          {...register("typeCodeId", { required: "Type is required" })}
        >
          {codes.length > 0 && (
            <>
              {codes
                .filter((c) => c.type === "ObservationType")
                .map((typeCode) => (
                  <option key={typeCode.id} value={typeCode.id}>
                    {typeCode.name}
                  </option>
                ))}
            </>
          )}
        </select>
      </div>
      <FilesSelector multiple={false} isUploading={isSaving} onImagesChange={(images: FormData | null) => {
        setSelectedFiles(images)
        if (images?.has('file0')) {
          const file = images.get('file0');

          // Ensure file is a File object and has a valid MIME type
          if (!(file instanceof File) || !file.type.startsWith('image/')) {
            console.warn('Invalid file type or not a File object');
            setGps(null);
            return;
          }
          extractGPSFromImage(file)
            .then((coords) => {
              console.log(coords.latitude && coords.longitude ? `Extracted GPS: Lat ${coords.latitude}, Lng ${coords.longitude}` : 'No GPS data found in image.');
              setGps(coords?.latitude != null && coords?.longitude != null ? { latitude: coords.latitude, longitude: coords.longitude } : null);
            })
            .catch((error) => {
              console.error('Error extracting GPS:', error);
              setGps(null);
            });
        }
      }} />

      {/* Google Map Pin Section */}
      <div className="mt-4">
          <GoogleMapPin
            coords={gps ?? { latitude: centreCoordinates?.latitude, longitude: centreCoordinates?.longitude }}
            onChange={(coords) => setGps(coords)}
          />
      </div>

      <div className="flex justify-end space-x-2">
        <button
          type="button"
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
          onClick={() => onClose(null, [])}
        >
          Cancel
        </button>
        <Button
          type="submit"
          disabled={isSaveDisabled}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          Save
        </Button>
      </div>
    </form>
  );
};

export default NewMapObservations;
